import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ApiError, getErrorMessage } from '@/shared/api/api-error';
import { cn } from '@/shared/lib/utils';

interface ErrorStateProps {
  error: unknown;
  /** Omit to hide the retry button for failures that retrying cannot fix. */
  onRetry?: () => void;
  title?: string;
  className?: string;
}

/**
 * Inline failure state for a panel, table or card.
 *
 * Uses the normalized `ApiError` to decide what to say and whether retrying is
 * even worth offering — a 403 will fail identically forever, while a network
 * blip very likely will not (§65).
 */
export function ErrorState({ error, onRetry, title, className }: ErrorStateProps) {
  const apiError = error instanceof ApiError ? error : null;
  const isOffline = apiError?.kind === 'network';
  const canRetry = onRetry && (apiError ? apiError.isRetryable : true);

  const Icon = isOffline ? WifiOff : AlertTriangle;

  const heading =
    title ??
    (isOffline
      ? 'Cannot reach the server'
      : apiError?.isForbidden
        ? 'You do not have access to this'
        : 'Something went wrong');

  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center justify-center px-6 py-14 text-center', className)}
    >
      <div className="bg-destructive/10 text-destructive flex size-11 items-center justify-center rounded-full">
        <Icon className="size-5" aria-hidden />
      </div>
      <p className="mt-4 text-sm font-semibold">{heading}</p>
      <p className="text-muted-foreground mt-1 max-w-sm text-xs leading-relaxed">
        {getErrorMessage(error)}
      </p>
      {canRetry && (
        <Button variant="outline" size="sm" className="mt-5" onClick={onRetry}>
          <RefreshCw className="size-3.5" aria-hidden />
          Try again
        </Button>
      )}
    </div>
  );
}
